import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import PublicNavigation from '../../components/PublicNavigation';
import './ReportBuilder.css';

function ReportBuilder() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFields, setSelectedFields] = useState([]);
  const [filters, setFilters] = useState({
    chapter: '',
    district: '',
    category: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  const AVAILABLE_FIELDS = [
    // Basic Info
    { id: 'category', label: 'Category', group: 'Basic' },
    { id: 'chapter', label: 'Chapter', group: 'Basic' },
    { id: 'district', label: 'District', group: 'Basic' },
    { id: 'donorName', label: 'Donor Name', group: 'Basic' },
    { id: 'donorType', label: 'Donor Type', group: 'Basic' },
    { id: 'projectCoordinator', label: 'Coordinator', group: 'Basic' },
    { id: 'projectDescription', label: 'Project Description', group: 'Basic' },
    { id: 'status', label: 'Status', group: 'Basic' },
    { id: 'startYear', label: 'Start Year', group: 'Basic' },
    { id: 'reportMonth', label: 'Report Month', group: 'Basic' },
    { id: 'reportYear', label: 'Report Year', group: 'Basic' },
    
    // Beneficiaries
    { id: 'totalBeneficiaries', label: 'Total Beneficiaries', group: 'Beneficiaries' },
    { id: 'beneficiaryName', label: 'Beneficiary Names (Schools)', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Baseline Test', label: 'Baseline Test Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Soft Skills', label: 'Soft Skills Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Career Aspiration', label: 'Career Aspiration Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Library', label: 'Library Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Uniform', label: 'Uniform Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Female Health and Hygiene', label: 'Female Health Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Nutritional Supplement', label: 'Nutritional Supplement Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategories.Teacher Training', label: 'Teacher Training Count', group: 'Beneficiaries' },
    { id: 'beneficiaryCategoryDates.Baseline Test', label: 'Baseline Test Date', group: 'Beneficiaries' },
    { id: 'beneficiaryCategoryDates.Soft Skills', label: 'Soft Skills Date', group: 'Beneficiaries' },
    { id: 'beneficiaryCategoryDates.Teacher Training', label: 'Teacher Training Date', group: 'Beneficiaries' },
    
    // Funds - NEW FORMAT (totalFunds)
    { id: 'totalFunds.received', label: 'Total Funds Received (₹)', group: 'Funds - Total' },
    { id: 'totalFunds.disbursed', label: 'Total Funds Disbursed (₹)', group: 'Funds - Total' },
    { id: 'totalFunds.openingBalance', label: 'Total Opening Balance (₹)', group: 'Funds - Total' },
    { id: 'totalFunds.closingBalance', label: 'Total Closing Balance (₹)', group: 'Funds - Total' },
    
    // Funds - OLD FORMAT (for backward compatibility)
    { id: 'fundReceived', label: 'Funds Received - OLD (₹)', group: 'Funds - Legacy' },
    { id: 'fundDisbursed', label: 'Funds Disbursed - OLD (₹)', group: 'Funds - Legacy' },
    { id: 'fundOpeningBalance', label: 'Opening Balance - OLD (₹)', group: 'Funds - Legacy' },
    { id: 'fundClosingBalance', label: 'Closing Balance - OLD (₹)', group: 'Funds - Legacy' },
    
    // Fund Sources (Individual)
    { id: 'fundSources.local.received', label: 'Local - Received (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.local.disbursed', label: 'Local - Disbursed (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.fcra.received', label: 'FCRA - Received (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.fcra.disbursed', label: 'FCRA - Disbursed (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.tnfusa.received', label: 'TNF USA - Received (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.tnfusa.disbursed', label: 'TNF USA - Disbursed (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.government.received', label: 'Government Grant - Received (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.government.disbursed', label: 'Government Grant - Disbursed (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.corporate.received', label: 'Corporate CSR - Received (₹)', group: 'Funds - By Source' },
    { id: 'fundSources.corporate.disbursed', label: 'Corporate CSR - Disbursed (₹)', group: 'Funds - By Source' },
    
    // Metadata
    { id: 'createdAt', label: 'Created Date', group: 'Metadata' },
    { id: 'createdBy', label: 'Created By', group: 'Metadata' },
    { id: 'lastModified', label: 'Last Modified', group: 'Metadata' }
  ];

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      const snap = await getDocs(collection(db, 'manualProjects'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProjects(data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }

  const getNestedValue = (obj, path) => {
    // Handle nested paths like 'beneficiaryCategories.Baseline Test'
    const parts = path.split('.');
    let value = obj;
    
    for (const part of parts) {
      if (value === null || value === undefined) return '';
      
      // Special handling for fundSources
      if (part === 'fundSources' && Array.isArray(value.fundSources)) {
        // Next part should be source name (local, fcra, etc.)
        const sourceName = parts[parts.indexOf(part) + 1];
        const field = parts[parts.indexOf(part) + 2]; // received or disbursed
        
        if (!sourceName || !field) return '';
        
        const source = value.fundSources.find(s => 
          s.source.toLowerCase().replace(/\s+/g, '') === sourceName.toLowerCase()
        );
        
        return source ? (source[field] || 0) : 0;
      }
      
      value = value[part];
    }
    
    // Format dates
    if (value && typeof value === 'object' && value.seconds) {
      return new Date(value.seconds * 1000).toLocaleDateString();
    }
    
    // Format numbers for fund fields
    if (typeof value === 'number' && path.includes('fund')) {
      return `₹${Math.round(value).toLocaleString('en-IN')}`;
    }
    
    return value !== null && value !== undefined ? value : '';
  };

  const getFilteredProjects = () => {
    return projects.filter(p => {
      if (filters.chapter && p.chapter !== filters.chapter) return false;
      if (filters.district && p.district !== filters.district) return false;
      if (filters.category && p.category !== filters.category) return false;
      if (filters.status && p.status !== filters.status) return false;
      
      if (filters.startDate) {
        const projectDate = new Date(p.reportYear, 
          ['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(p.reportMonth));
        if (projectDate < new Date(filters.startDate)) return false;
      }
      
      if (filters.endDate) {
        const projectDate = new Date(p.reportYear, 
          ['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(p.reportMonth));
        if (projectDate > new Date(filters.endDate)) return false;
      }
      
      return true;
    });
  };

  const toggleField = (fieldId) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };

  const exportToCSV = () => {
    const filtered = getFilteredProjects();
    
    if (selectedFields.length === 0) {
      alert('Please select at least one field');
      return;
    }
    
    if (filtered.length === 0) {
      alert('No projects match your filters');
      return;
    }

    // Create headers
    const headers = selectedFields.map(fieldId => {
      const field = AVAILABLE_FIELDS.find(f => f.id === fieldId);
      return field ? field.label : fieldId;
    });

    // Create rows
    const rows = filtered.map(project => {
      return selectedFields.map(fieldId => {
        const value = getNestedValue(project, fieldId);
        // Escape quotes and wrap in quotes for CSV
        return `"${String(value).replace(/"/g, '""')}"`;
      });
    });

    // Combine into CSV
    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const filteredProjects = getFilteredProjects();
  const uniqueChapters = [...new Set(projects.map(p => p.chapter).filter(Boolean))].sort();
  const uniqueDistricts = [...new Set(projects.map(p => p.district).filter(Boolean))].sort();
  const uniqueCategories = [...new Set(projects.map(p => p.category).filter(Boolean))].sort();

  // Group fields by category
  const fieldGroups = AVAILABLE_FIELDS.reduce((acc, field) => {
    if (!acc[field.group]) acc[field.group] = [];
    acc[field.group].push(field);
    return acc;
  }, {});

  if (loading) return (
    <div className="report-builder-page">
      <PublicNavigation />
      <div className="loading">Loading...</div>
    </div>
  );

  return (
    <div className="report-builder-page">
      <PublicNavigation />
      
      <div className="report-builder-container">
        <div className="builder-header">
          <h1>📊 Custom Report Builder</h1>
          <p>Select fields and filters to create your custom report</p>
        </div>

        <div className="builder-layout">
          {/* Left Panel - Field Selection */}
          <div className="fields-panel">
            <div className="panel-header">
              <h3>Select Fields ({selectedFields.length})</h3>
              <button onClick={() => setSelectedFields([])} className="clear-btn">Clear All</button>
            </div>
            
            <div className="field-groups">
              {Object.entries(fieldGroups).map(([groupName, fields]) => (
                <div key={groupName} className="field-group">
                  <h4>{groupName}</h4>
                  <div className="field-list">
                    {fields.map(field => (
                      <label key={field.id} className="field-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field.id)}
                          onChange={() => toggleField(field.id)}
                        />
                        <span>{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Filters & Preview */}
          <div className="preview-panel">
            <div className="filters-section">
              <h3>Filters</h3>
              <div className="filter-grid">
                <div className="filter-item">
                  <label>Chapter</label>
                  <select value={filters.chapter} onChange={(e) => setFilters({...filters, chapter: e.target.value})}>
                    <option value="">All Chapters</option>
                    {uniqueChapters.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="filter-item">
                  <label>District</label>
                  <select value={filters.district} onChange={(e) => setFilters({...filters, district: e.target.value})}>
                    <option value="">All Districts</option>
                    {uniqueDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                <div className="filter-item">
                  <label>Category</label>
                  <select value={filters.category} onChange={(e) => setFilters({...filters, category: e.target.value})}>
                    <option value="">All Categories</option>
                    {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="filter-item">
                  <label>Status</label>
                  <select value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="Planning">Planning</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>

                <div className="filter-item">
                  <label>Start Date</label>
                  <input type="date" value={filters.startDate} onChange={(e) => setFilters({...filters, startDate: e.target.value})} />
                </div>

                <div className="filter-item">
                  <label>End Date</label>
                  <input type="date" value={filters.endDate} onChange={(e) => setFilters({...filters, endDate: e.target.value})} />
                </div>
              </div>
            </div>

            <div className="preview-section">
              <div className="preview-header">
                <h3>Preview ({filteredProjects.length} projects)</h3>
                <button onClick={exportToCSV} className="export-btn" disabled={selectedFields.length === 0}>
                  📥 Export to CSV
                </button>
              </div>

              {selectedFields.length === 0 ? (
                <div className="empty-state">
                  <p>👈 Select fields from the left panel to preview your report</p>
                </div>
              ) : (
                <div className="table-scroll">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {selectedFields.map(fieldId => {
                          const field = AVAILABLE_FIELDS.find(f => f.id === fieldId);
                          return <th key={fieldId}>{field ? field.label : fieldId}</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProjects.slice(0, 10).map(project => (
                        <tr key={project.id}>
                          {selectedFields.map(fieldId => (
                            <td key={fieldId}>{getNestedValue(project, fieldId)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredProjects.length > 10 && (
                    <div className="preview-note">
                      Showing first 10 of {filteredProjects.length} projects. Export to see all.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportBuilder;
